// Venue Dashboard Data Generator
// This creates realistic dashboard data for each venue

import { REAL_VENUE_DATA } from '../constants/realVenueData';

// Helper functions to generate realistic data
const generateBookingData = (venueId, venueName) => {
  const baseBookings = Math.floor(Math.random() * 8) + 2; // 2-10 bookings
  const statusCounts = {};
  
  // Generate realistic status distribution
  statusCounts.open = Math.floor(baseBookings * 0.4);
  statusCounts.accepted = Math.floor(baseBookings * 0.3);
  statusCounts.completed = Math.floor(baseBookings * 0.2);
  statusCounts.cancelled = baseBookings - statusCounts.open - statusCounts.accepted - statusCounts.completed;
  
  return {
    all: baseBookings,
    ...statusCounts
  };
};

const generatePerformanceData = (venue) => {
  const baseRevenue = venue.hourlyRate * 20; // Base revenue calculation
  const growthPercentage = Math.floor(Math.random() * 50) + 10; // 10-60% growth
  
  return {
    bookingsCompleted: {
      current: Math.floor(Math.random() * 50) + 15,
      previous: Math.floor(Math.random() * 30) + 10,
      change: growthPercentage
    },
    totalRevenue: {
      current: baseRevenue + Math.floor(Math.random() * 10000),
      previous: baseRevenue - Math.floor(Math.random() * 5000),
      change: growthPercentage
    }
  };
};

const generateStaffData = (venue) => {
  // Different staff requirements based on venue type/size
  const capacity = venue.maxCapacity || 50;
  const staffMultiplier = capacity > 100 ? 1.2 : capacity > 50 ? 1.0 : 0.8;
  
  return {
    frontOfHouse: {
      current: Math.floor(Math.random() * 6) + 2,
      total: Math.floor(8 * staffMultiplier)
    },
    kitchen: {
      current: Math.floor(Math.random() * 8) + 4,
      total: Math.floor(12 * staffMultiplier)
    },
    floorStaff: {
      current: Math.floor(Math.random() * 10) + 6,
      total: Math.floor(15 * staffMultiplier)
    }
  };
};

const generateTasks = (venueId, venueName) => {
  const taskTypes = [
    'Send confirmation message',
    'Review booking documents',
    'Prepare venue setup',
    'Follow up with client',
    'Update availability calendar',
    'Process payment',
    'Arrange catering',
    'Setup equipment'
  ];
  
  const names = [
    'Jannet Terry', 'Robbie Fowler', 'Jamie Murray', 'Sarah Johnson',
    'Mike Rodriguez', 'Jennifer Chen', 'Maria Garcia', 'David Thompson'
  ];
  
  const tasks = [];
  const taskCount = Math.floor(Math.random() * 5) + 2; // 2-6 tasks
  
  for (let i = 0; i < taskCount; i++) {
    const daysFromNow = Math.floor(Math.random() * 14) + 1;
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + daysFromNow);
    
    tasks.push({
      id: i + 1,
      bookingRef: `B-${Math.floor(Math.random() * 9000) + 1000}`,
      title: taskTypes[Math.floor(Math.random() * taskTypes.length)],
      assignee: names[Math.floor(Math.random() * names.length)],
      dueTime: `${Math.floor(Math.random() * 12) + 9}:${Math.floor(Math.random() * 2) * 30} ${Math.random() > 0.5 ? 'AM' : 'PM'}`,
      dueDate: dueDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    });
  }
  
  return tasks;
};

const generateTodaysBookings = (venue) => {
  const events = [
    'All-Team Kickoff', 'Client Meeting', 'Birthday Party', 'Corporate Event',
    'Workshop Session', 'Networking Event', 'Product Launch', 'Training Session',
    'Team Building', 'Company Retreat', 'Annual Meeting', 'Celebration Party'
  ];
  
  const bookings = [];
  const timeSlots = ['10:00 AM', '12:00 PM', '2:00 PM', '4:00 PM', '6:00 PM', '8:00 PM'];
  const statuses = ['open', 'accepted', 'completed', 'cancelled'];
  const colors = ['blue', 'green', 'red'];
  
  const bookingCount = Math.floor(Math.random() * 4) + 2; // 2-5 bookings
  
  for (let i = 0; i < bookingCount; i++) {
    const timeSlot = timeSlots[Math.floor(Math.random() * timeSlots.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const color = colors[Math.floor(Math.random() * colors.length)];
    
    bookings.push({
      id: i + 1,
      time: timeSlot,
      title: events[Math.floor(Math.random() * events.length)],
      status: status,
      color: color
    });
  }
  
  return bookings.sort((a, b) => {
    const timeA = new Date(`2000-01-01 ${a.time}`);
    const timeB = new Date(`2000-01-01 ${b.time}`);
    return timeA - timeB;
  });
};

// Generate dashboard data for a specific venue
export const generateVenueDashboardData = (venueId, venueName, venue) => {
  return {
    venueName: venueName || venue?.name || 'Unknown Venue',
    bookings: generateBookingData(venueId, venueName),
    performance: generatePerformanceData(venue),
    staffAvailability: generateStaffData(venue),
    tasks: generateTasks(venueId, venueName),
    todaysBookings: generateTodaysBookings(venue)
  };
};

// Pre-generated data for existing venues
export const VENUE_DASHBOARD_DATA = {
  // Existing venues from realVenueData.js
  1: generateVenueDashboardData(1, 'The Cherish Club', REAL_VENUE_DATA[0]),
  2: generateVenueDashboardData(2, 'Anomalous Space', REAL_VENUE_DATA[1]),
  3: generateVenueDashboardData(3, 'Laundry Studios', REAL_VENUE_DATA[2]),
  4: generateVenueDashboardData(4, 'Missionworks', REAL_VENUE_DATA[3]),
  5: generateVenueDashboardData(5, 'The Hearth', REAL_VENUE_DATA[4]),
  6: generateVenueDashboardData(6, 'The Gladstone Arms', REAL_VENUE_DATA[5]),
  
  // Special venue for dugisharma2013@gmail.com
  'dugisharma2013@gmail.com': generateVenueDashboardData(
    'dugisharma2013@gmail.com',
    'Dugi\'s Event Space',
    {
      id: 'dugisharma2013@gmail.com',
      name: 'Dugi\'s Event Space',
      maxCapacity: 75,
      hourlyRate: 85,
      address: '123 Innovation Drive, Tech District, London',
      city: 'London',
      description: 'Modern event space perfect for tech meetups, workshops, and corporate events.',
      vibe: ['Modern', 'Tech-focused', 'Professional'],
      facilities: ['Wi-Fi', 'Projector', 'Sound System', 'Whiteboard', 'Catering Kitchen'],
      amenities: {
        foodBeverage: {
          inHouseCatering: [
            { name: 'Tech-friendly catering', available: true },
            { name: 'Complimentary coffee and snacks', available: true }
          ],
          externalCatering: [
            { name: 'External catering allowed', available: true }
          ],
          alcohol: {
            venueProvides: false,
            byoAllowed: true,
            corkageFee: true
          }
        }
      }
    }
  )
};

// API-like function to get dashboard data
export const getVenueDashboardData = (venueIdentifier) => {
  // If it's an email, use that as the key
  if (venueIdentifier.includes('@')) {
    return VENUE_DASHBOARD_DATA[venueIdentifier] || VENUE_DASHBOARD_DATA[1];
  }
  
  // If it's a numeric ID, convert to number
  const venueId = parseInt(venueIdentifier);
  return VENUE_DASHBOARD_DATA[venueId] || VENUE_DASHBOARD_DATA[1];
};

// Function to get all venue dashboard data (for admin purposes)
export const getAllVenueDashboardData = () => {
  return VENUE_DASHBOARD_DATA;
};

// Function to update dashboard data (for when we switch to real APIs)
export const updateVenueDashboardData = (venueIdentifier, newData) => {
  if (venueIdentifier.includes('@')) {
    VENUE_DASHBOARD_DATA[venueIdentifier] = { ...VENUE_DASHBOARD_DATA[venueIdentifier], ...newData };
  } else {
    const venueId = parseInt(venueIdentifier);
    VENUE_DASHBOARD_DATA[venueId] = { ...VENUE_DASHBOARD_DATA[venueId], ...newData };
  }
};

// Mock API functions (to be replaced with real API calls)
export const venueDashboardAPI = {
  // Get dashboard data
  getDashboardData: async (venueIdentifier) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return getVenueDashboardData(venueIdentifier);
  },
  
  // Update booking status
  updateBookingStatus: async (venueIdentifier, bookingId, newStatus) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const data = getVenueDashboardData(venueIdentifier);
    // Update the booking status in the data
    const booking = data.todaysBookings.find(b => b.id === bookingId);
    if (booking) {
      booking.status = newStatus;
    }
    return data;
  },
  
  // Add new task
  addTask: async (venueIdentifier, taskData) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const data = getVenueDashboardData(venueIdentifier);
    const newTask = {
      id: data.tasks.length + 1,
      ...taskData
    };
    data.tasks.push(newTask);
    return data;
  },
  
  // Update staff availability
  updateStaffAvailability: async (venueIdentifier, staffData) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const data = getVenueDashboardData(venueIdentifier);
    data.staffAvailability = { ...data.staffAvailability, ...staffData };
    return data;
  }
};

export default VENUE_DASHBOARD_DATA;
